import { useState, useRef, ChangeEvent } from 'react'
import { Paperclip, X, Upload, FileIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { toast } from 'sonner'
import { useOrgPath } from '@/lib/pathFor'

interface Attachment {
  id: string
  filename: string
  mime_type: string
  size: number
}

interface AttachmentPickerProps {
  emailId: string
  attachments: Attachment[]
  onAttachmentsChange: (attachments: Attachment[]) => void
  disabled?: boolean
}

const MAX_FILE_SIZE = 25 * 1024 * 1024 // 25MB

function csrfToken(): string {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export default function AttachmentPicker({ emailId, attachments, onAttachmentsChange, disabled }: AttachmentPickerProps) {
  const [uploading, setUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const pathFor = useOrgPath()

  function handleFileSelect(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      toast.error(`File too large (max 25MB)`)
      return
    }

    uploadFile(file)

    // Reset input so same file can be selected again
    e.target.value = ''
  }

  async function uploadFile(file: File) {
    setUploading(true)

    const formData = new FormData()
    formData.append('file', file)

    try {
      const response = await fetch(pathFor(`/emails/${emailId}/attachments`), {
        method: 'POST',
        body: formData,
        headers: { 'X-CSRF-Token': csrfToken() }
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Upload failed')
      }

      const attachment = await response.json()
      onAttachmentsChange([...attachments, attachment])
      toast.success('Attachment added')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to upload attachment')
    } finally {
      setUploading(false)
    }
  }

  async function removeAttachment(attachmentId: string) {
    try {
      const response = await fetch(pathFor(`/attachments/${attachmentId}`), {
        method: 'DELETE',
        headers: { 'X-CSRF-Token': csrfToken() }
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Remove failed')
      }

      onAttachmentsChange(attachments.filter(a => a.id !== attachmentId))
      toast.success('Attachment removed')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to remove attachment')
    }
  }

  return (
    <div className="space-y-2">
      {/* Attachment list */}
      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {attachments.map((attachment) => (
            <div
              key={attachment.id}
              className="flex items-center gap-2 rounded-md border bg-muted/50 px-3 py-1.5 text-xs"
            >
              <FileIcon className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="max-w-[150px] truncate">{attachment.filename}</span>
              <span className="text-muted-foreground">({formatFileSize(attachment.size)})</span>
              {!disabled && (
                <button
                  onClick={() => removeAttachment(attachment.id)}
                  className="ml-1 text-muted-foreground hover:text-red-600 cursor-pointer"
                  title="Remove attachment"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Upload button */}
      {!disabled && (
        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileSelect}
            className="hidden"
            disabled={uploading}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 text-xs cursor-pointer"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? (
              <>
                <Upload className="h-3 w-3 mr-1.5 animate-pulse" />
                Uploading...
              </>
            ) : (
              <>
                <Paperclip className="h-3 w-3 mr-1.5" />
                Attach File
              </>
            )}
          </Button>
          <span className="text-[10px] text-muted-foreground">Max 25MB</span>
        </div>
      )}
    </div>
  )
}
